import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { handleChangeEventInput } from "helpers/Helpers";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import axios, { CancelToken } from "axios";
import { IPostAccommodationUnit, PostAccommodationUnit } from "api/rpc/2024-04/facilityAdmin/accommodation/units";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationBuildings } from "api/rpc/2024-04/facilityAdmin/accommodation/building";
import { IAccommodationBuilding } from "../buildings/Buildings";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";

export function NewUnit() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [unit, setUnit] = useState<IPostAccommodationUnit>({
    title: "",
    building_id: undefined,
    unit_type_id: undefined,
    code: "",
    floor: "",
    status: "active",
  });

  const [accommodationUnitTypes, setAccommodationUnitTypes] = useState(undefined);
  const [accommodationBuildings, setAccommodationBuildings] = useState(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationBuildings();
    void loadAccommodationUnitTypes();
    return () => {
      source.cancel();
    };
  }, []);

  async function loadAccommodationUnitTypes(token?: CancelToken) {
    if (accommodationUnitTypes !== undefined) {
      setAccommodationUnitTypes(undefined);
    }
    const accommodationUnitTypesRes = await GetAccommodationUnitTypes(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationUnitTypesRes.status !== StatusCode.OK) {
      setAccommodationUnitTypes([]);
      return;
    }

    setAccommodationUnitTypes(accommodationUnitTypesRes.data);
  }

  async function loadAccommodationBuildings(token?: CancelToken) {
    if (accommodationBuildings !== undefined) {
      setAccommodationBuildings(undefined);
    }
    const accommodationBuildingsRes = await GetAccommodationBuildings(null, false);

    if (token && token.reason) {
      return;
    }
    if (accommodationBuildingsRes.status !== StatusCode.OK) {
      setAccommodationBuildings([]);
      return;
    }

    setAccommodationBuildings(accommodationBuildingsRes.data);
  }

  async function createAccommodationUnit() {
    console.log(unit);

    const unitRes = await PostAccommodationUnit(
      {
        title: unit?.title,
        building_id: unit?.building_id,
        unit_type_id: unit?.unit_type_id,
        code: unit?.code,
        floor: unit?.floor,
        status: unit?.status,
      },

      true,
    );

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError("Error creating new unit"));
      return;
    }

    dispatch(showSuccess(t("Successfully created unit")));
    history.push("/admin/accommodation/configuration/units");
  }

  return (
    <Page
      title={t("Add New Unit")}
      subtitle={t("Enter the key details for your unit")}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation" },
        { label: t("Property Configuration"), url: "/admin/accommodation/configuration/units" },
        { label: t("Units"), url: "/admin/accommodation/configuration/units" },
      ]}
    >
      <Form>
        <Card title="Unit details">
          <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
            <hr style={{ marginBottom: "0px" }} />
          </div>

          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={unit?.title}
                  label={t("Name of unit")}
                  id="title"
                  onChange={e => handleChangeEventInput(e, unit, setUnit)}
                  placeholder={t("Enter unit name")}
                />
                <Input
                  value={unit?.floor}
                  label={t("Floor")}
                  id="floor"
                  onChange={e => handleChangeEventInput(e, unit, setUnit)}
                  placeholder={t("Enter floor")}
                />

                <Input
                  value={unit?.code}
                  label={t("Code")}
                  id="code"
                  onChange={e => handleChangeEventInput(e, unit, setUnit)}
                  placeholder={"Enter code"}
                  type={"text"}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Select
                  label={t("Building")}
                  onChange={(value: number) => setUnit(prev => ({ ...prev, building_id: Number(value) }))}
                  defaultValue={unit?.building_id}
                  placeholder="Select building"
                  showPlaceholder
                >
                  {accommodationBuildings?.map((building: IAccommodationBuilding) => (
                    <Select.Option key={building.id} value={building.id} name={building.title}>
                      {building.title}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  label={t("Unit type")}
                  onChange={(value: number) => setUnit(prev => ({ ...prev, unit_type_id: Number(value) }))}
                  defaultValue={unit?.unit_type_id}
                  placeholder="Select unit type"
                  showPlaceholder
                >
                  {accommodationUnitTypes?.map((unitType: IAccommodationUnitType) => (
                    <Select.Option key={unitType.id} value={unitType.id} name={unitType.title}>
                      {unitType.title}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>

        <FormLayout>
          <FormLayout.Group>
            <div></div>
            <div></div>
            <div className="flex justify-end mt-3">
              <Button type="secondary" onClick={() => history.push("/admin/accommodation/configuration/units")}>
                {"Cancel"}
              </Button>

              <Button type="primary" onClick={() => createAccommodationUnit()}>
                {"Publish"}
              </Button>
            </div>
          </FormLayout.Group>
        </FormLayout>
      </Form>
    </Page>
  );
}
